"use client";

import { cn } from "@frontend/shared/lib/utils";
import { Loader2 } from "lucide-react";
import { type ButtonHTMLAttributes, forwardRef } from "react";

export interface KioskButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "outline" | "ghost" | "outline-inverse";
  size?: "sm" | "md" | "lg";
  isLoading?: boolean;
  fullWidth?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

export const KioskButton = forwardRef<HTMLButtonElement, KioskButtonProps>(
  (
    {
      className,
      variant = "primary",
      size = "lg",
      isLoading = false,
      fullWidth = true,
      disabled,
      children,
      leftIcon,
      rightIcon,
      ...props
    },
    ref,
  ) => {
    return (
      <button
        ref={ref}
        disabled={disabled || isLoading}
        className={cn(
          // Base styles - matching design spec
          "inline-flex items-center justify-center gap-2 rounded-full font-base",
          "transition-all duration-200 ease-in-out",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
          "min-h-[40px]",
          // Size variants - lg matches 59px height from design spec
          size === "sm" && "h-10 px-6 text-sm",
          size === "md" && "h-12 px-8 text-base",
          size === "lg" &&
            "h-12 px-6 py-3 text-base md:h-[60px] md:px-9 md:py-4 md:text-lg",

          // Color variants - using #292E70 as primary, #000 for secondary
          variant === "primary" && [
            "bg-kioskPrimary text-white border border-transparent",
            "hover:bg-black hover:border hover:border-black hover:bg-blend-overlay",
            "active:bg-black active:border active:border-black active:bg-blend-overlay",
            "focus-visible:ring-kioskPrimary",
            "disabled:pointer-events-none  disabled:opacity-50",
          ],
          variant === "secondary" && [
            "relative text-white",
            // Multiple backgrounds: gradient overlay + black, blended with overlay mode
            "bg-[image:linear-gradient(rgba(0,0,0,0.8),rgba(0,0,0,0.8)),linear-gradient(transparent,transparent)]",
            "bg-blend-overlay backdrop-blur-md",
            "hover:bg-black hover:bg-blend-normal hover:backdrop-blur-none",
            "active:bg-black active:bg-blend-normal active:backdrop-blur-none",
            "focus-visible:ring-black",
            "disabled:pointer-events-none  disabled:opacity-50",
          ],
          variant === "outline" && [
            "bg-transparent text-kioskPrimary",
            "border border-kioskPrimary",
            "hover:bg-kioskPrimary/10 active:bg-kioskPrimary/20",
            "focus-visible:ring-kioskPrimary",
            "disabled:pointer-events-none disabled:opacity-50",
          ],
          variant === "outline-inverse" && [
            "bg-transparent text-white",
            "border border-white",
            "hover:bg-white/10 active:bg-white/20",
            "focus-visible:ring-white",
            "disabled:pointer-events-none disabled:opacity-50",
          ],
          variant === "ghost" && [
            "bg-transparent text-kioskPrimary",
            "hover:bg-kioskPrimary/10 active:bg-kioskPrimary/20",
            "focus-visible:ring-kioskPrimary",
            "disabled:pointer-events-none disabled:opacity-50",
          ],

          // Width - flex-1 for proper flex behavior
          fullWidth && "w-full flex-1",

          // Add flex when icons are present for proper alignment
          leftIcon || rightIcon
            ? "flex"
            : "inline-block truncate whitespace-nowrap",

          className,
        )}
        {...props}
      >
        {isLoading && <Loader2 className="h-5 w-5 animate-spin" />}
        {!isLoading && leftIcon && (
          <span className="inline-flex shrink-0">{leftIcon}</span>
        )}
        {children}
        {!isLoading && rightIcon && (
          <span className="inline-flex shrink-0">{rightIcon}</span>
        )}
      </button>
    );
  },
);

KioskButton.displayName = "KioskButton";
